/**
 * Per-kind behaviour flags.
 *
 * ALL callback-specific logic lives in this table.  The contact machine and
 * session manager consult it — no `getType() === 'queue_callback'` checks
 * exist anywhere else.
 */

import type { ContactKind } from './types';

export interface KindPolicy {
  /** Keep the incoming modal visible after onAccepted (callbacks show a
   *  "dialing customer" timer instead of closing). */
  keepModalOnAccept: boolean;
  /** Don't call contact.accept() from the modal — Connect already auto-
   *  accepted this contact kind. */
  skipAcceptOnModalAccept: boolean;
  /** Set agent NOT_ROUTABLE after a missed contact of this kind.  Callbacks
   *  are auto-accepted so the agent didn't miss anything. */
  setNotRoutableOnMissed: boolean;
  /** Auto-accept on delivery (non-inbound, non-monitor, non-dialpad). */
  autoAcceptOnDelivery: boolean;
  /** Route to afterCallWork on ended (chat only — voice keeps session for
   *  post-call documentation). */
  routeToAcwOnEnded: boolean;
  /** Show the incoming modal for this kind (monitor contacts don't). */
  showIncomingModal: boolean;
}

const VOICE: KindPolicy = {
  keepModalOnAccept: false,
  skipAcceptOnModalAccept: false,
  setNotRoutableOnMissed: true,
  autoAcceptOnDelivery: true, // non-inbound non-dialpad voice → auto-accept
  routeToAcwOnEnded: false,
  showIncomingModal: true,
};

const CALLBACK: KindPolicy = {
  keepModalOnAccept: true, // show "dialing customer" timer
  skipAcceptOnModalAccept: true, // Connect auto-accepted — contact.accept() fails
  setNotRoutableOnMissed: false, // agent missed nothing
  autoAcceptOnDelivery: false, // Connect handles callback accept
  routeToAcwOnEnded: false,
  showIncomingModal: true,
};

const CHAT: KindPolicy = {
  keepModalOnAccept: false,
  skipAcceptOnModalAccept: false,
  setNotRoutableOnMissed: true,
  autoAcceptOnDelivery: false,
  routeToAcwOnEnded: true, // chat always routes to ACW on ended
  showIncomingModal: true,
};

const MONITOR: KindPolicy = {
  keepModalOnAccept: false,
  skipAcceptOnModalAccept: false,
  setNotRoutableOnMissed: false,
  autoAcceptOnDelivery: false,
  routeToAcwOnEnded: false,
  showIncomingModal: false, // monitor contacts never show a modal
};

export const KIND_POLICY: Record<ContactKind, KindPolicy> = {
  voice: VOICE,
  callback: CALLBACK,
  chat: CHAT,
  monitor: MONITOR,
};

// ---------------------------------------------------------------------------
// Classification helper
// ---------------------------------------------------------------------------

/**
 * Classify a Connect contact into a ContactKind.
 * Called ONCE per contact at registration time.
 */
export function classifyContact(
  contact: { getType?: () => string },
  isMonitor: boolean
): ContactKind {
  const t = contact.getType?.();
  if (isMonitor) return 'monitor';
  if (t === 'queue_callback') return 'callback';
  if (t === 'chat') return 'chat';
  return 'voice';
}
